// Copyright (c) 2022. Heusala Group Oy <info@heusalagroup.fi>. All rights reserved.

import { SmsTokenDTO } from "../../../../../../../io/hyperify/core/auth/sms/types/SmsTokenDTO";
import { AuthSessionService } from "../../../../../../../io/hyperify/frontend/services/AuthSessionService";
import { MY_INVENTORY_ITEM_VIEW_CLASS_NAME } from "../../../../../core/constants/className";
import { TranslationFunction } from "../../../../../../../io/hyperify/core/types/TranslationFunction";
import { useParams } from "react-router-dom";
import { useMyInventoryItem } from "../../../../hooks/useMyInventoryItem";
import {
    T_MY_INVENTORY_ITEM_VIEW_TITLE,
    T_MY_INVENTORY_ITEM_VIEW_DATE_LABEL,
    T_MY_INVENTORY_ITEM_VIEW_INVENTORY_ITEM_ID_LABEL,
    T_MY_INVENTORY_ITEM_VIEW_NO_RESULTS_FOUND,
    T_DATE_FORMAT,
    T_MY_INVENTORY_ITEM_VIEW_END_DATE_LABEL,
    T_MY_INVENTORY_ITEM_VIEW_SUMMARY_LABEL,
    T_MY_INVENTORY_ITEM_VIEW_STATE_LABEL, T_MY_INVENTORY_ITEM_VIEW_SAVING_ERROR_MESSAGE
} from "../../../../../core/constants/translation";
import { TranslationParams } from "../../../../../../../io/hyperify/core/types/TranslationParams";
import { Loader } from "../../../../../../../io/hyperify/frontend/components/loader/Loader";
import { CardGrid } from "../../../../../../../io/hyperify/frontend/components/cardGrid/CardGrid";
import { DateCard } from "../../../../../../../io/hyperify/frontend/components/card/date/DateCard";
import { TextCard } from "../../../../../../../io/hyperify/frontend/components/card/text/TextCard";
import { InventoryState } from "../../../../../../../io/hyperify/core/store/types/inventory/InventoryState";
import { getInventoryStateTranslationKey } from "../../../../../../../io/hyperify/core/store/constants/storeTranslation";
import { MyInventoryData } from "./data/MyInventoryData";
import { InventoryData } from "../../../../../../../io/hyperify/core/store/types/inventory/data/InventoryData";
import { useCallback, useState } from "react";
import { SendanorClientHttpService } from "../../../../../core/services/SendanorClientHttpService";
import { LogService } from "../../../../../../../io/hyperify/core/LogService";
import { EmailTokenDTO } from "../../../../../../../io/hyperify/core/auth/email/types/EmailTokenDTO";
import { InventoryItemDTO } from "../../../../../../../io/hyperify/core/store/types/inventory/InventoryItemDTO";
import { useMyClientPermissionList } from "../../../../hooks/useMyClientPermissionList";
import "./MyInventoryItemView.scss";

const LOG = LogService.createLogger('MyInventoryItemView');

export enum MyInventoryItemViewState {
    READY   = "READY",
    SAVING  = "SAVING",
    ERROR   = "ERROR"
}

export interface MyInventoryItemViewProps {
    readonly className ?: string;
    readonly t          : TranslationFunction;
}

export function MyInventoryItemView (props: MyInventoryItemViewProps) {
    const t = props?.t;
    const className = props?.className;
    const params = useParams();
    const clientId = params?.clientId;
    const inventoryItemId = params?.inventoryItemId;
    const [viewState, setViewState] = useState<MyInventoryItemViewState>(MyInventoryItemViewState.READY);
    const [item, refreshCallback] = useMyInventoryItem(inventoryItemId, clientId);
    const [permissions, refreshPermissions] = useMyClientPermissionList(clientId);
    const itemState = item?.state ?? InventoryState.UNINITIALIZED;
    const notFound = item === null;
    const isLoading = item === undefined || !permissions;
    const translationParams : TranslationParams = {
        INVENTORY_ITEM_ID: inventoryItemId ?? ''
    };
    const dateFormat = t(T_DATE_FORMAT);

    const saveInventoryDataCallback = useCallback(
        (value: InventoryData, state: InventoryState) => {

            if (viewState === MyInventoryItemViewState.SAVING) {
                LOG.warn(`Component already saving`);
                return;
            }

            const token : EmailTokenDTO | SmsTokenDTO | undefined = AuthSessionService.getToken();
            if (item && token && clientId && inventoryItemId) {

                const newItemDto : InventoryItemDTO = {
                    ...item,
                    state: state,
                    data: value
                };

                setViewState(MyInventoryItemViewState.SAVING);
                SendanorClientHttpService.updateMyInventoryItemById(clientId, inventoryItemId, token, newItemDto).then(
                    (newValue) => {
                        setViewState(MyInventoryItemViewState.READY);
                        LOG.info(`Successfully saved item; refreshing models again`);
                        refreshCallback();
                        refreshPermissions();
                    }
                ).catch((err : any) => {
                    LOG.error(`Error: `, err);
                    setViewState(MyInventoryItemViewState.ERROR);
                });

            } else {
                LOG.error(`No item, token, clientId or inventoryItemId defined: `, item, token, clientId, inventoryItemId);
            }
        },
        [
            item,
            clientId,
            inventoryItemId,
            refreshCallback,
            refreshPermissions,
            viewState,
            setViewState
        ]
    );
    return (
        <div className={
            MY_INVENTORY_ITEM_VIEW_CLASS_NAME
            + (className? ` ${className}` : '')
        }>
            <h3>{t(T_MY_INVENTORY_ITEM_VIEW_TITLE, translationParams)}</h3>
            <div className={`${MY_INVENTORY_ITEM_VIEW_CLASS_NAME}-details`}>
                {isLoading? <Loader /> : null}
                {notFound ? (
                    <p className={`${MY_INVENTORY_ITEM_VIEW_CLASS_NAME}-details-error-message`}>{t(T_MY_INVENTORY_ITEM_VIEW_NO_RESULTS_FOUND, translationParams)}</p>
                ) : null}
                {!notFound && !isLoading && permissions?
                    <>
                        <CardGrid>
                            <TextCard label={t(T_MY_INVENTORY_ITEM_VIEW_INVENTORY_ITEM_ID_LABEL)} value={item?.inventoryItemId} />
                            <TextCard label={t(T_MY_INVENTORY_ITEM_VIEW_SUMMARY_LABEL)} value={item?.summary} />
                            <TextCard label={t(T_MY_INVENTORY_ITEM_VIEW_STATE_LABEL)} value={t(getInventoryStateTranslationKey(itemState))} />
                        </CardGrid>
                        <CardGrid>
                            <DateCard label={t(T_MY_INVENTORY_ITEM_VIEW_DATE_LABEL)} value={item?.date} format={dateFormat} />
                            <DateCard label={t(T_MY_INVENTORY_ITEM_VIEW_END_DATE_LABEL)} value={item?.endDate} format={dateFormat} />
                        </CardGrid>

                        <MyInventoryData
                            t={t}
                            permissions={permissions}
                            type={item?.productType}
                            state={item?.state}
                            value={item?.data}
                            submit={saveInventoryDataCallback}
                        />

                        {viewState === MyInventoryItemViewState.ERROR ? (
                            <p className={`${MY_INVENTORY_ITEM_VIEW_CLASS_NAME}-details-error-message`}>{t(T_MY_INVENTORY_ITEM_VIEW_SAVING_ERROR_MESSAGE)}</p>
                        ) : null}

                        { isLoading || (viewState === MyInventoryItemViewState.SAVING) ? <>
                            <Loader className={`${MY_INVENTORY_ITEM_VIEW_CLASS_NAME}-details-loader`} />
                        </> : null}

                    </>
                    : null}
            </div>
        </div>
    );
}
